import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle, CreditCard, Download, Info, ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/sonner';
import { useBilling } from '@/hooks/useBilling';
import { billingService, Plan } from '@/services/billingService';
import { teamService, Team } from '@/services/teamService';
import { useQuery } from '@tanstack/react-query';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { TraditionalPaymentForm } from '@/components/TraditionalPaymentForm';
import { formatLocalDate } from '@/lib/date';

// Initialize Stripe only when this component is loaded
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const Billing = () => {
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [setupIntentClientSecret, setSetupIntentClientSecret] = useState<string | null>(null);
  const [paymentMethodDialogOpen, setPaymentMethodDialogOpen] = useState(false);
  const [subscriptionProcessed, setSubscriptionProcessed] = useState(false);

  // Use the billing hook with enabled set to true to load data
  const {
    subscription,
    paymentMethod,
    invoices,
    isLoading,
    error,
  } = useBilling();

  // Handle URL parameters for Stripe Checkout success/cancel
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const canceled = urlParams.get('canceled');
    const subscriptionParam = urlParams.get('subscription');

    if (success === 'true') {
      toast.success('Subscription updated successfully!');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (canceled === 'true') {
      toast.info('Subscription update was canceled');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [subscriptionProcessed]);



  // Load team data to get current subscription
  const {
    data: team,
    isLoading: isTeamLoading,
    error: teamError,
  } = useQuery<Team>({
    queryKey: ['team'],
    queryFn: () => teamService.getTeam(),
  });

  // Load plans
  const {
    data: plans,
    isLoading: isPlansLoading,
    error: plansError,
  } = useQuery<Plan[]>({
    queryKey: ['plans'],
    queryFn: () => billingService.getPlans(),
  });

  // Handle subscription upgrade (now that 'plans' is declared above)
  const handleSubscriptionUpgrade = useCallback(async (subscriptionNumber: string) => {
    try {
      const subscriptionNumberToPlan: Record<string, string> = {
        '1': 'Snapshot (Free)',
        '2': 'Archive',
        '3': 'Chronicle',
        '4': 'Infinity',
        '5': 'Enterprise'
      };

      const planName = subscriptionNumberToPlan[subscriptionNumber];
      if (!planName) return;
      if (planName === 'Snapshot (Free)') return;
      if (!plans) return;

      const targetPlan = plans.find(plan => plan.name === planName);
      if (!targetPlan) {
        toast.error(`Plan "${planName}" not found. Please try again later.`);
        return;
      }

      const response = await billingService.createCheckoutSession(targetPlan.price.id, true);
      if (response && response.url) {
        localStorage.removeItem('selected-subscription');
        window.location.href = response.url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create checkout session');
    }
  }, [plans]);

  // Handle subscription parameter when plans are loaded
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const subscriptionParam = urlParams.get('subscription');

    if (subscriptionParam && ['1', '2', '3', '4', '5'].includes(subscriptionParam) && plans && !isPlansLoading && !subscriptionProcessed) {
      console.log('Billing page - plans loaded, processing subscription parameter:', subscriptionParam);
      setSubscriptionProcessed(true);
      handleSubscriptionUpgrade(subscriptionParam);
      // Clean up URL parameter to prevent reprocessing
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('subscription');
      window.history.replaceState({}, document.title, newUrl.pathname + newUrl.search);
      // Also clean up localStorage to prevent reprocessing
      localStorage.removeItem('selected-subscription');
    }
  }, [plans, isPlansLoading, subscriptionProcessed, handleSubscriptionUpgrade]);

  // Reset subscriptionProcessed when component mounts
  useEffect(() => {
    setSubscriptionProcessed(false);
  }, []);

  // Helper function to get subscription tier name from subscription object
  const getSubscriptionTierName = (subscription) => {
    if (!subscription?.items?.data?.[0]?.price?.product?.name) {
      return 'Snapshot (Free)';
    }
    return subscription.items.data[0].price.product.name;
  };

  // Helper function to check if a tier is the current subscription
  const isCurrentSubscription = (tierName: string): boolean => {
    if (tierName === 'Snapshot (Free)') {
      // If no subscription exists, consider it as "Free" subscription
      return !subscription;
    }

    if (!subscription) return false;
    const currentTierName = getSubscriptionTierName(subscription);
    return tierName === currentTierName;
  };

  // Helper function to get the subscription tier number
  const getSubscriptionTierNumber = (tierName: string): number => {
    switch (tierName) {
      case 'Snapshot (Free)': return 1;
      case 'Archive': return 2;
      case 'Chronicle': return 3;
      case 'Infinity': return 4;
      case 'Enterprise': return 5;
      default: return 0;
    }
  };

  // Helper function to check if this is an upgrade
  const isUpgrade = (targetTierName: string): boolean => {
    if (!subscription) {
      // If no subscription, any tier other than Free is an upgrade
      return targetTierName !== 'Snapshot (Free)';
    }

    const currentTierName = getSubscriptionTierName(subscription);
    const currentTierNumber = getSubscriptionTierNumber(currentTierName);
    const targetTierNumber = getSubscriptionTierNumber(targetTierName);
    return targetTierNumber > currentTierNumber;
  };

  // Helper function to find plan by name
  const findPlanByName = (planName: string): Plan | null => {
    if (!plans) return null;
    return plans.find(plan => plan.name === planName) || null;
  };

  // Helper functions for add-on management
  const getCurrentAddOn = (): string | null => {
    if (!subscription?.items?.data) return null;

    // Map price IDs to tier names
    const priceIdToTier: Record<string, string> = {
      'price_1ReMryGCnkZbvT2CGxL5bpkR': 'Tier 1',
      'price_1ReMt5GCnkZbvT2CJHWO2atY': 'Tier 2',
      'price_1ReMtcGCnkZbvT2CMfEgBBxD': 'Tier 3',
      'price_1ReMu2GCnkZbvT2Cv5WkDzgx': 'Tier 4',
      "price_1ReR3vGCnkZbvT2CEWLzL3bE": "Tier 1",
      "price_1ReR4WGCnkZbvT2C4IsTfkcs": "Tier 2",
      "price_1ReR57GCnkZbvT2C27GELNNP": "Tier 3",
      "price_1ReR5iGCnkZbvT2CFfTq1TLo": "Tier 4",
      "price_1RoamqGCnkZbvT2CLH7OlsRY": "Tier 1",
      "price_1RoanIGCnkZbvT2CnmkDbELL": "Tier 2",
      "price_1RoaovGCnkZbvT2CnZRiBnqB": "Tier 3"
    };

    // Look for add-on items in the subscription
    const addOnItem = subscription.items.data.find(item =>
      priceIdToTier[item.price?.id] ||
      item.price?.product?.name?.includes('Tier') ||
      item.price?.product?.name?.includes('Summarization')
    );

    if (addOnItem?.price?.id && priceIdToTier[addOnItem.price.id]) {
      return priceIdToTier[addOnItem.price.id];
    }

    return addOnItem?.price?.product?.name || null;
  };

  const isCurrentAddOn = (tierName: string): boolean => {
    const currentAddOn = getCurrentAddOn();
    return currentAddOn === tierName;
  };

  const getAddOnTierNumber = (tierName: string): number => {
    switch (tierName) {
      case 'Tier 1': return 1;
      case 'Tier 2': return 2;
      case 'Tier 3': return 3;
      case 'Tier 4': return 4;
      default: return 0;
    }
  };

  const isAddOnUpgrade = (targetTierName: string): boolean => {
    const currentAddOn = getCurrentAddOn();
    if (!currentAddOn) return true; // If no current add-on, any selection is an upgrade

    const currentTierNumber = getAddOnTierNumber(currentAddOn);
    const targetTierNumber = getAddOnTierNumber(targetTierName);
    return targetTierNumber > currentTierNumber;
  };

  const handleAddOnAction = async (tierName: string, isUpgrade: boolean) => {
    try {
      // Map tier names to tier numbers
      const tierToNumber: Record<string, number> = {
        'Tier 1': 1,
        'Tier 2': 2,
        'Tier 3': 3,
        'Tier 4': 4
      };

      const tierNumber = tierToNumber[tierName];
      if (!tierNumber) {
        toast.error(`Tier number not found for ${tierName}`);
        return;
      }

      const response = await billingService.createAddOnCheckoutSession(tierNumber, isUpgrade);
      if (response && response.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Error creating add-on checkout session:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create checkout session');
    }
  };

  const handleCancelSubscription = async () => {
    try {
      await billingService.cancelSubscription(subscription?.id);
      toast.success('Subscription cancelled successfully');
      setCancelDialogOpen(false);
      // Refetch subscription data
      window.location.reload();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Failed to cancel subscription');
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      await billingService.reactivateSubscription(subscription?.id);
      toast.success('Subscription reactivated successfully');
      // Refetch subscription data
      window.location.reload();
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      toast.error('Failed to reactivate subscription');
    }
  };

  const handleUpdatePaymentMethod = async () => {
    try {
      const response = await billingService.createSetupIntent();
      setSetupIntentClientSecret(response.clientSecret);
      setPaymentMethodDialogOpen(true);
    } catch (error) {
      console.error('Error updating payment method:', error);
      toast.error('Failed to update payment method');
    }
  };

  const handlePaymentMethodSuccess = () => {
    toast.success('Payment method updated successfully');
    setPaymentMethodDialogOpen(false);
    setSetupIntentClientSecret(null);
    // Refetch payment method data
    window.location.reload();
  };

  const handlePaymentMethodError = (error: string) => {
    toast.error(error);
  };

  const handleUpgradeDowngrade = async (plan: Plan, isUpgrade: boolean) => {
    try {
      const response = await billingService.createCheckoutSession(plan.price.id, isUpgrade);
      if (response && response.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create checkout session');
    }
  };

  const handlePlanAction = (planName: string, isUpgrade: boolean) => {
    const plan = findPlanByName(planName);
    if (!plan) {
      toast.error(`Plan "${planName}" not found. Please try again later.`);
      return;
    }
    handleUpgradeDowngrade(plan, isUpgrade);
  };

  const handleDownloadInvoice = async (invoiceId: string) => {
    try {
      const blob = await billingService.downloadInvoice(invoiceId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download invoice');
    }
  };

  if (isLoading || isPlansLoading || isTeamLoading) {
    return (
      <PageLayout title="Billing & Accounts" description="Manage your subscription, account information, and authorized users">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading billing information...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error || plansError || teamError) {
    return (
      <PageLayout title="Billing & Accounts" description="Manage your subscription, account information, and authorized users">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-500 mb-4">Error: {error?.message || plansError?.message || teamError?.message}</p>
            <Button onClick={() => window.location.reload()} variant="outline">
              Try Again
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Billing & Accounts" description="Manage your subscription, account information, and authorized users">
      <div className="grid gap-6">
        {/* Subscription Tiers */}
        <Card>
          <CardHeader>
            <CardTitle>Subscription Tiers</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tiers</TableHead>
                  <TableHead>Credits</TableHead>
                  <TableHead>Memories Extracted</TableHead>
                  <TableHead>Memory Sources</TableHead>
                  <TableHead>TTL Max</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className={isCurrentSubscription('Snapshot (Free)') ? 'bg-recallio-light-gray/20 border-recallio-green' : ''}>
                  <TableCell className="font-medium">
                    Snapshot (Free)
                    {isCurrentSubscription('Snapshot (Free)') && (
                      <Badge variant="default" className="ml-2 bg-recallio-green">Current</Badge>
                    )}
                  </TableCell>
                  <TableCell>1,000</TableCell>
                  <TableCell>500</TableCell>
                  <TableCell>5</TableCell>
                  <TableCell>30d</TableCell>
                  <TableCell>$0</TableCell>
                  <TableCell className="text-right">
                    {!isCurrentSubscription('Snapshot (Free)') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCancelDialogOpen(true)}
                      >
                        Downgrade
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow className={isCurrentSubscription('Archive') ? 'bg-recallio-light-gray/20 border-recallio-green' : ''}>
                  <TableCell className="font-medium">
                    Archive
                    {isCurrentSubscription('Archive') && (
                      <Badge variant="default" className="ml-2 bg-recallio-green">Current</Badge>
                    )}
                  </TableCell>
                  <TableCell>2,000</TableCell>
                  <TableCell>5,000</TableCell>
                  <TableCell>25</TableCell>
                  <TableCell>1Y</TableCell>
                  <TableCell>$49</TableCell>
                  <TableCell className="text-right">
                    {!isCurrentSubscription('Archive') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePlanAction('Archive', isUpgrade('Archive'))}
                      >
                        {isUpgrade('Archive') ? 'Upgrade' : 'Downgrade'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow className={isCurrentSubscription('Chronicle') ? 'bg-recallio-light-gray/20 border-recallio-green' : ''}>
                  <TableCell className="font-medium">
                    Chronicle
                    {isCurrentSubscription('Chronicle') && (
                      <Badge variant="default" className="ml-2 bg-recallio-green">Current</Badge>
                    )}
                  </TableCell>
                  <TableCell>10,000</TableCell>
                  <TableCell>25,000</TableCell>
                  <TableCell>50</TableCell>
                  <TableCell>1Y</TableCell>
                  <TableCell>$99</TableCell>
                  <TableCell className="text-right">
                    {!isCurrentSubscription('Chronicle') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePlanAction('Chronicle', isUpgrade('Chronicle'))}
                      >
                        {isUpgrade('Chronicle') ? 'Upgrade' : 'Downgrade'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow className={isCurrentSubscription('Infinity') ? 'bg-recallio-light-gray/20 border-recallio-green' : ''}>
                  <TableCell className="font-medium">
                    Infinity
                    {isCurrentSubscription('Infinity') && (
                      <Badge variant="default" className="ml-2 bg-recallio-green">Current</Badge>
                    )}
                  </TableCell>
                  <TableCell>30,000</TableCell>
                  <TableCell>100,000</TableCell>
                  <TableCell>75</TableCell>
                  <TableCell>1Y</TableCell>
                  <TableCell>$270</TableCell>
                  <TableCell className="text-right">
                    {!isCurrentSubscription('Infinity') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePlanAction('Infinity', isUpgrade('Infinity'))}
                      >
                        {isUpgrade('Infinity') ? 'Upgrade' : 'Downgrade'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow className={isCurrentSubscription('Enterprise') ? 'bg-recallio-light-gray/20 border-recallio-green' : ''}>
                  <TableCell className="font-medium">
                    Enterprise
                    {isCurrentSubscription('Enterprise') && (
                      <Badge variant="default" className="ml-2 bg-recallio-green">Current</Badge>
                    )}
                  </TableCell>
                  <TableCell>Unlimited</TableCell>
                  <TableCell>Unlimited</TableCell>
                  <TableCell>Unlimited</TableCell>
                  <TableCell>Unlimited</TableCell>
                  <TableCell></TableCell>
                  <TableCell className="text-right">
                    {!isCurrentSubscription('Enterprise') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open('mailto:support@recall.io', '_blank')}
                      >
                        Discuss Pricing
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  Any credit overages will be billed on the next billing cycle at&nbsp;
                  <span className="font-medium text-foreground">
                   $10 per 1,000 credits.
                  </span>
                </p>
              </div>
            </div>


            {/* Subscription Information */}
            {subscription && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="space-y-4">
                  {/* Next Payment Date */}
                  {!subscription.cancel_at && (
                    <p className="text-muted-foreground">
                      Next payment on{' '}
                      <span className="font-medium text-foreground">
                        {(() => {
                          const periodEnd = subscription.items?.data?.[0]?.current_period_end;

                          if (periodEnd && (typeof periodEnd === 'number' || !isNaN(Number(periodEnd)))) {
                            const timestamp = typeof periodEnd === 'number' ? periodEnd : Number(periodEnd);
                            return formatLocalDate(timestamp * 1000);
                          }

                          return 'N/A';
                        })()}
                      </span>
                    </p>
                  )}

                  {/* Canceled Subscription Warning */}
                  {subscription.cancel_at && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-red-800">
                          <p className="font-medium mb-1">Subscription Canceled</p>
                          <p>
                            Your subscription will end on{' '}
                            <span className="font-bold">
                              {formatLocalDate(subscription.cancel_at * 1000)}
                            </span>
                            . You can reactivate it anytime before this date.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    {subscription.cancel_at ? (
                      <Button
                        className="bg-recallio-green hover:bg-recallio-green/90"
                        onClick={handleReactivateSubscription}
                      >
                        Reactivate Subscription
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => setCancelDialogOpen(true)}
                      >
                        Cancel Subscription
                      </Button>
                    )}
                    <Button asChild variant="outline">
                      <Link to="/usage-stats" className="flex items-center gap-2">
                        View Usage Stats
                        <ArrowRight size={14} />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add-on Summarization Packs */}
        <Card>
          <CardHeader>
            <CardTitle>Add-on Graph Memory Packs</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tier</TableHead>
                  <TableHead># of Graph Memory Nodes</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className={isCurrentAddOn('Tier 1') ? 'bg-recallio-light-gray/20 border-recallio-green' : ''}>
                  <TableCell className="font-medium">
                    Tier 1
                    {isCurrentAddOn('Tier 1') && (
                      <Badge variant="default" className="ml-2 bg-recallio-green">Current</Badge>
                    )}
                  </TableCell>
                  <TableCell>100,000</TableCell>
                  <TableCell>$29</TableCell>
                  <TableCell className="text-right">
                    {!isCurrentAddOn('Tier 1') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddOnAction('Tier 1', isAddOnUpgrade('Tier 1'))}
                      >
                        {isAddOnUpgrade('Tier 1') ? 'Upgrade' : 'Downgrade'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow className={isCurrentAddOn('Tier 2') ? 'bg-recallio-light-gray/20 border-recallio-green' : ''}>
                  <TableCell className="font-medium">
                    Tier 2
                    {isCurrentAddOn('Tier 2') && (
                      <Badge variant="default" className="ml-2 bg-recallio-green">Current</Badge>
                    )}
                  </TableCell>
                  <TableCell>200,000</TableCell>
                  <TableCell>$58</TableCell>
                  <TableCell className="text-right">
                    {!isCurrentAddOn('Tier 2') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddOnAction('Tier 2', isAddOnUpgrade('Tier 2'))}
                      >
                        {isAddOnUpgrade('Tier 2') ? 'Upgrade' : 'Downgrade'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow className={isCurrentAddOn('Tier 3') ? 'bg-recallio-light-gray/20 border-recallio-green' : ''}>
                  <TableCell className="font-medium">
                    Tier 3
                    {isCurrentAddOn('Tier 3') && (
                      <Badge variant="default" className="ml-2 bg-recallio-green">Current</Badge>
                    )}
                  </TableCell>
                  <TableCell>500,000</TableCell>
                  <TableCell>$125</TableCell>
                  <TableCell className="text-right">
                    {!isCurrentAddOn('Tier 3') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddOnAction('Tier 3', isAddOnUpgrade('Tier 3'))}
                      >
                        {isAddOnUpgrade('Tier 3') ? 'Upgrade' : 'Downgrade'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow className={isCurrentAddOn('Enterprise') ? 'bg-recallio-light-gray/20 border-recallio-green' : ''}>
                  <TableCell className="font-medium">
                    Enterprise
                    {isCurrentAddOn('Enterprise') && (
                      <Badge variant="default" className="ml-2 bg-recallio-green">Current</Badge>
                    )}
                  </TableCell>
                  <TableCell>Unlimited</TableCell>
                  <TableCell></TableCell>
                  <TableCell className="text-right">
                    {!isCurrentAddOn('Enterprise') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open('mailto:support@recall.io', '_blank')}
                      >
                        Discuss Pricing
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Payment Method */}
        <Card>
          <CardHeader>
            <CardTitle>Payment Method</CardTitle>
            <CardDescription>Your current payment method</CardDescription>
          </CardHeader>
          <CardContent>
            {paymentMethod ? (
              <div className="flex items-center gap-3 bg-recallio-medium-gray p-3 rounded-md border border-recallio-light-gray/50">
                <CreditCard className="text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium truncate">
                    {paymentMethod.card?.brand ?
                      paymentMethod.card.brand.charAt(0).toUpperCase() + paymentMethod.card.brand.slice(1) :
                      'Card'} •••• {paymentMethod.card?.last4 || '****'}
                  </p>
                  <p className="text-sm text-muted-foreground truncate">
                    Expires {paymentMethod.card?.exp_month?.toString().padStart(2, '0') || '**'}/{paymentMethod.card?.exp_year?.toString().slice(-2) || '**'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                No payment method added
              </div>
            )}
          </CardContent>
          <CardFooter className="flex flex-col items-stretch gap-2">
            <Button variant="outline" className="w-full" onClick={handleUpdatePaymentMethod}>
              {paymentMethod ? 'Update Payment Method' : 'Add Payment Method'}
            </Button>
          </CardFooter>
        </Card>

        {/* Invoices */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Invoices</CardTitle>
            <CardDescription>Your recent billing history</CardDescription>
          </CardHeader>
          <CardContent>
            {invoices && invoices.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell>
                        {formatLocalDate(invoice.created * 1000)}
                      </TableCell>
                      <TableCell>
                        ${(invoice.amount_paid / 100).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={invoice.status === 'paid' ? 'default' : 'secondary'}>
                          {invoice.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadInvoice(invoice.id)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                No invoices found
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Payment Dialogs */}
      {/* Payment Method Dialog */}
      {setupIntentClientSecret && (
        <Dialog open={paymentMethodDialogOpen} onOpenChange={setPaymentMethodDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Payment Method</DialogTitle>
              <DialogDescription>
                Enter your new payment details below
              </DialogDescription>
            </DialogHeader>

            <Elements stripe={stripePromise} options={{ clientSecret: setupIntentClientSecret }}>
              <TraditionalPaymentForm
                clientSecret={setupIntentClientSecret}
                onSuccess={handlePaymentMethodSuccess}
                onError={handlePaymentMethodError}
              />
            </Elements>
          </DialogContent>
        </Dialog>
      )}

      {/* Cancel Subscription Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Subscription</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel your subscription?
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">Important Information</p>
                  <p>
                    Your service will remain active until{' '}
                    <span className="font-medium">
                      {(() => {
                        const periodEnd = subscription?.items?.data?.[0]?.current_period_end;
                        if (periodEnd && (typeof periodEnd === 'number' || !isNaN(Number(periodEnd)))) {
                          const timestamp = typeof periodEnd === 'number' ? periodEnd : Number(periodEnd);
                          return formatLocalDate(timestamp * 1000);
                        }
                        return 'the end of your current billing period';
                      })()}
                    </span>
                    . You won't be charged again, but you'll continue to have access to all features until then.
                  </p>
                </div>
              </div>
            </div>

            <div className="text-sm text-muted-foreground">
              <p>After cancellation, you can resubscribe at any time to regain access to premium features.</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
              Keep Subscription
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelSubscription}
            >
              Cancel Subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default Billing;
